import type { FeedCommentPreview, FeedItem } from "@compass/shared/schema/api/feed";
import type { FavoriteTarget } from "@compass/shared/schema/favorite";
import { Link } from "@tanstack/react-router";
import { MessageSquare } from "lucide-react";
import type { ReactNode } from "react";
import anonymousAvatarSvg from "assets/avatars/anonymous.svg";
import { Avatar, ProseDocument, Tooltip } from "components/elements";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { formatISODateRelative } from "lib/formatISODate";

type PostComments = Extract<FeedItem, { type: "postCommentsAdded" }>;
type BlogPostComments = Extract<FeedItem, { type: "blogPostCommentsAdded" }>;
type VendorComments = Extract<FeedItem, { type: "vendorCommentsAdded" }>;

type Props = PostComments | BlogPostComments | VendorComments;

function ParentLink({
    item,
    className,
    children,
}: {
    item: Props;
    className?: string;
    children: ReactNode;
}) {
    switch (item.type) {
        case "postCommentsAdded":
            return (
                <Link
                    to="/social/post/$id"
                    params={{ id: String(item.postId) }}
                    className={className}
                >
                    {children}
                </Link>
            );
        case "blogPostCommentsAdded":
            return (
                <Link to="/p/$slug" params={{ slug: item.blogPostSlug }} className={className}>
                    {children}
                </Link>
            );
        case "vendorCommentsAdded":
            return (
                <Link
                    to="/vendors/$id"
                    params={{ id: String(item.vendorId) }}
                    className={className}
                >
                    {children}
                </Link>
            );
    }
}

function getParentLabel(item: Props): string {
    switch (item.type) {
        case "postCommentsAdded":
            return item.postTitle ?? `Post #${item.postId}`;
        case "blogPostCommentsAdded":
            return item.blogPostTitle;
        case "vendorCommentsAdded":
            return item.vendorName;
    }
}

function getParentFavoriteTarget(item: Props): FavoriteTarget {
    switch (item.type) {
        case "postCommentsAdded":
            return { type: "post", id: item.postId };
        case "blogPostCommentsAdded":
            return { type: "blogPost", id: item.blogPostId };
        case "vendorCommentsAdded":
            return { type: "vendor", id: item.vendorId };
    }
}

const CommentRow = ({ comment }: { comment: FeedCommentPreview }) => {
    const author = comment.author;
    const realName = author.type === "anonymous" ? author.realName : undefined;
    const avatarUrl =
        author.type === "anonymous"
            ? anonymousAvatarSvg
            : author.type === "identified" || author.type === "anonymous-self"
              ? author.avatarUrl
              : null;
    const initials = author.type === "identified" ? author.initials : "?";
    // Only link to /profile/$id when the author is a live, socially-visible
    // identified user. Anonymous / anonymous-self / deleted / compass-team /
    // social-disabled stay as plain text.
    const profileId = author.type === "identified" && author.isSocialEnabled ? author.id : null;

    const avatarEl = (
        <Avatar
            className="h-7 w-7 shrink-0 bg-slate-200 text-[10px] text-slate-400"
            photoUrl={avatarUrl}
            name={author.name}
            initials={initials}
            seed={comment.id}
        />
    );

    const nameEl = realName ? (
        <Tooltip content={realName}>
            <span className="cursor-help text-xs font-semibold text-gray-900 underline decoration-gray-400 decoration-dashed underline-offset-2">
                {author.name}
            </span>
        </Tooltip>
    ) : (
        <span className="text-xs font-semibold text-gray-900">{author.name}</span>
    );

    return (
        <div className="flex gap-2 py-2">
            {profileId !== null ? (
                <Link to="/profile/$id" params={{ id: String(profileId) }} className="shrink-0">
                    {avatarEl}
                </Link>
            ) : (
                avatarEl
            )}
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1">
                    {profileId !== null ? (
                        <Link
                            to="/profile/$id"
                            params={{ id: String(profileId) }}
                            className="no-underline hover:underline"
                        >
                            {nameEl}
                        </Link>
                    ) : (
                        nameEl
                    )}
                    <span className="text-[10px] text-gray-400">
                        {formatISODateRelative(comment.createdAt)}
                    </span>
                </div>
                <div className="prose prose-sm line-clamp-3 text-sm text-gray-700">
                    <ProseDocument doc={comment.contentJson} />
                </div>
            </div>
        </div>
    );
};

export const FeedCommentsCard = (props: Props) => {
    const label = getParentLabel(props);
    const count = props.comments.length;

    // For posts, PostService.createPost stores the post body as the first
    // ThreadComment (PostService.ts), so `comments` always includes one
    // synthetic body entry on top of actual replies. Blog post and vendor
    // groups don't have that — every entry is a real reply.
    const renderCountText = () => {
        if (props.type === "postCommentsAdded") {
            const replyCount = count - 1;
            if (replyCount <= 0) return "new post";
            if (replyCount === 1) return "1 new comment on";
            return `${replyCount} new comments on`;
        }
        if (count === 1) return "1 new comment on";
        return `${count} new comments on`;
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-gray-50/50 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-1.5">
                <MessageSquare className="h-4 w-4 text-gray-500" />
                <span className="text-xs text-gray-500">{renderCountText()}</span>
                <ParentLink
                    item={props}
                    className="text-xs font-semibold text-blue-600 hover:underline"
                >
                    {label}
                </ParentLink>
                <div className="ml-auto">
                    <FavoriteButton target={getParentFavoriteTarget(props)} size={14} />
                </div>
            </div>
            <div className="divide-y divide-gray-100">
                {props.comments.slice(0, 3).map((comment) => (
                    <CommentRow key={comment.id} comment={comment} />
                ))}
            </div>
            {count > 3 && (
                <ParentLink
                    item={props}
                    className="mt-1 block text-xs text-blue-500 hover:underline"
                >
                    View all {count} comments →
                </ParentLink>
            )}
        </div>
    );
};
